import React, { memo, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useTags } from "modules/providers/tags-provider";
import {
  HashIcon,
  AtSignIcon,
  RestoreIcon,
  InfoIcon,
  ExternalIcon,
  PencilOutlineIcon,
  TrashOutlineIcon,
  GroupMembershipIcon,
} from "modules/icons";

import {
  sortUsersByEmailAddress,
  sortUsersByFullName,
} from "modules/utils/Functions";
import moment from "moment";
import { MONTH_DAY_YEAR_FORMAT } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import {
  DRAWER_TYPE,
  MODAL_TYPE,
} from "modules/providers/overlay-provider/overlay-provider";
import DistributionGroupTypeIcon from "modules/admin/distribution-group-type-icon";
import { isEmpty } from "lodash";
import AdminDistributionGroupHistoricalAccess from "./admin-distribution-groups/admin-distribution-group-historical-access";
import AdminDistributionGroupMemberList from "./admin-distribution-groups/admin-distribution-group-member-list";
import AdminDistributionGroupMembershipCriteria from "./admin-distribution-groups/admin-distribution-group-membership-criteria";

interface IProps {
  distributionGroupId: string;
}

const AdminDistributionGroupDrawer = ({ distributionGroupId }: IProps) => {
  const { distributionGroupsById } = useDistributionGroups();
  const { openModal, openDrawer } = useOverlay();
  const { getUsersById } = useUsers();
  const { getTagsById } = useTags();
  const scrollRef = useRef<HTMLDivElement>(null);

  const onEditClicked = useCallback(() => {
    openDrawer({
      type: DRAWER_TYPE.ADMIN_ADD_EDIT_DISTRIBUTION_GROUP,
      id: distributionGroupId,
    });
  }, [openDrawer, distributionGroupId]);

  const onArchiveGroupClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CONFIRM_ARCHIVE_DISTRIBUTION_GROUP,
      ids: [distributionGroupId],
    });
  }, [openModal, distributionGroupId]);

  const onUnarchiveGroupClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CONFIRM_UNARCHIVE_DISTRIBUTION_GROUP,
      ids: [distributionGroupId],
    });
  }, [openModal, distributionGroupId]);

  const distributionGroup = useMemo(
    () => distributionGroupsById[distributionGroupId],
    [distributionGroupId, distributionGroupsById],
  );

  const users = useMemo(() => {
    if (!distributionGroup) return [];
    return getUsersById(distributionGroup.userIdList).sort(sortUsersByFullName);
  }, [distributionGroup, getUsersById]);

  const emailUsers = useMemo(() => {
    if (!distributionGroup) return [];
    return getUsersById(distributionGroup.emailUserIdList).sort(
      sortUsersByEmailAddress,
    );
  }, [distributionGroup, getUsersById]);

  const tagList = useMemo(() => {
    if (!distributionGroup) return [];
    //SYN-2216: Only display active tags
    return getTagsById(distributionGroup.tagIdList).filter((tag) => tag.active);
  }, [distributionGroup, getTagsById]);

  if (!distributionGroup) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton top={5} left={5} color={colors.gray30} />
      <DrawerHeader>
        <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
          Group Details
        </Center>
      </DrawerHeader>
      <DrawerBody
        py={0}
        px={8}
        w="full"
        h="full"
        ref={scrollRef}
        flex={1}
        overflowY="auto"
      >
        <VStack w="full" alignItems="flex-start" flex={1} spacing={8}>
          <HStack w="full" spacing={2}>
            <Text
              fontSize={24}
              fontWeight={700}
              fontStyle={distributionGroup.active ? "normal" : "italic"}
              color={
                distributionGroup.active
                  ? colors.blackSecondary
                  : colors.grayMain
              }
            >
              <AtSignIcon
                boxSize={8}
                fill={
                  distributionGroup.active
                    ? colors.blackSecondary
                    : colors.grayMain
                }
              />
              {distributionGroup.name}
            </Text>
            <DistributionGroupTypeIcon
              distributionGroup={distributionGroup}
              iconProps={{ boxSize: 8, fill: colors.blackSecondary }}
            />
            {!!distributionGroup.emailUserIdList.length && (
              <Box pl={2}>
                <ExternalIcon fill={colors.transparent} />
              </Box>
            )}
          </HStack>

          <VStack w="full" alignItems="flex-start" spacing={3}>
            <HStack
              w="full"
              borderBottomColor={colors.black}
              borderBottomWidth={1}
            >
              <InfoIcon boxSize={6} />
              <Text fontWeight={600} fontSize={18}>
                Group Info
              </Text>
              <Spacer />
              {distributionGroup.active && (
                <Button
                  variant="ghost"
                  size="xs"
                  color={colors.orangePrimary}
                  fontSize={12}
                  fontWeight={500}
                  onClick={onEditClicked}
                  leftIcon={<PencilOutlineIcon fill={colors.orangePrimary} />}
                  h={6}
                >
                  Edit
                </Button>
              )}
            </HStack>
            {tagList.length > 0 && (
              <>
                <VStack w="full" alignItems="flex-start" spacing={1}>
                  <Text fontWeight={700} fontSize={16}>
                    Tag(s)
                  </Text>
                  <Wrap>
                    {tagList.map((tag) => (
                      <WrapItem key={tag.id}>
                        <HStack
                          spacing={1}
                          paddingX={2}
                          borderRadius={16}
                          borderWidth={1}
                          fontWeight={700}
                          fontSize={13}
                          borderColor={colors.grayPrimary}
                          color={colors.blue}
                        >
                          <HashIcon boxSize="11px" fill={colors.gray50} />
                          <Text
                            color={colors.gray50}
                          >{` ${tag.displayName}`}</Text>
                        </HStack>
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
                <Divider borderBottomColor={colors.gray0} />
              </>
            )}
            {!isEmpty(distributionGroup.description) && (
              <>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <Text fontWeight={700} fontSize={15}>
                    Description
                  </Text>
                  <Text fontSize={14} color={colors.gray50}>
                    {distributionGroup.description}
                  </Text>
                </VStack>
                <Divider borderBottomColor={colors.gray0} />
              </>
            )}
            <HStack w="full">
              <Text
                fontSize={14}
                fontWeight={600}
                color={colors.blackSecondary}
              >
                Date Created
              </Text>
              <Spacer />
              <Text fontSize={14} fontWeight={400} color={colors.gray50}>
                {moment(distributionGroup.createTime).format(
                  MONTH_DAY_YEAR_FORMAT,
                )}
              </Text>
            </HStack>
            <Divider borderBottomColor={colors.gray0} />
            {distributionGroup.active === false &&
              distributionGroup.inactiveTime !== null && (
                <HStack w="full" pt={4}>
                  <Text fontSize={14} fontWeight={600}>
                    Archived
                  </Text>
                  <Spacer />
                  <Text fontSize={14} color={colors.gray50}>
                    {moment(distributionGroup.inactiveTime).format(
                      MONTH_DAY_YEAR_FORMAT,
                    )}
                  </Text>
                </HStack>
              )}
            <HStack w="full">
              {distributionGroup.active ? (
                <Button
                  size="xxs"
                  leftIcon={
                    <TrashOutlineIcon boxSize={5} fill={colors.gray40} />
                  }
                  onClick={onArchiveGroupClicked}
                  variant="ghost"
                  color={colors.gray40}
                  w="fit-content"
                  alignSelf="flex-end"
                  fontSize={12}
                >
                  Archive Group
                </Button>
              ) : (
                <Button
                  height={5}
                  p={0}
                  m={0}
                  leftIcon={<RestoreIcon boxSize={5} fill={colors.gray40} />}
                  onClick={onUnarchiveGroupClicked}
                  variant="ghost"
                  color={colors.gray40}
                  w="fit-content"
                  alignSelf="flex-end"
                  fontSize={12}
                >
                  Unarchive Group
                </Button>
              )}
            </HStack>
          </VStack>

          <VStack w="full" alignItems="flex-start" spacing={4}>
            <HStack
              w="full"
              borderBottomColor={colors.black}
              borderBottomWidth={1}
            >
              <GroupMembershipIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontWeight={600} fontSize={18}>
                Membership
              </Text>
            </HStack>
            <AdminDistributionGroupMembershipCriteria
              distributionGroup={distributionGroup}
            />
            <AdminDistributionGroupHistoricalAccess
              distributionGroup={distributionGroup}
            />
          </VStack>
        </VStack>
        <AdminDistributionGroupMemberList
          customScrollParent={scrollRef}
          distributionGroup={distributionGroup}
          users={users}
          emailUsers={emailUsers}
        />
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AdminDistributionGroupDrawer);
